/**
 * SourcePanel — Source spritesheet view with frame grid overlay and
 * interactive bounding-box handles for the AnimationPreview editor.
 *
 * Also contains pixel analysis utilities: computeFrameBounds,
 * computeExtendedBounds, floodFillBounds, and autoFitFrames.
 */

import Phaser from 'phaser';
import { drawTechRect } from '@/ui/TechUI';
import type { AnimPreviewState } from './AnimPreviewState';
import type { AnimFrameDef } from '../AnimationPreviewScene';

// ── Layout constants (mirrored from main scene) ──
// These must stay in sync with AnimationPreviewScene layout.
import {
  SHEET_X, SHEET_Y, SHEET_SIZE, FRAME_SIZE,
} from './layoutConstants';

/**
 * Callbacks the SourcePanel needs from the host scene to trigger refreshes.
 */
export interface SourcePanelCallbacks {
  autoSave(): void;
  refreshPreviewSprite(frameOrderIdx: number): void;
  refreshFrameProps(): void;
  refreshSheetView(): void;
}

export class SourcePanel {
  private scene: Phaser.Scene;
  private state: AnimPreviewState;
  private cb: SourcePanelCallbacks;

  // Sheet view
  sheetPanelContainer!: Phaser.GameObjects.Container;
  private sheetViewContainer: Phaser.GameObjects.Container | null = null;

  // Bounding box overlay
  private bboxContainer: Phaser.GameObjects.Container | null = null;
  private bboxGfx: Phaser.GameObjects.Graphics | null = null;
  private bboxLabel: Phaser.GameObjects.Text | null = null;
  private bboxHandles: Phaser.GameObjects.Rectangle[] = [];
  private bboxMoveHit: Phaser.GameObjects.Rectangle | null = null;
  private bboxDragging: 'move' | 'tl' | 'tr' | 'bl' | 'br' | null = null;
  private bboxDragStart = { x: 0, y: 0, bx: 0, by: 0, bw: 0, bh: 0 };
  bboxFrameIdx = -1;

  constructor(scene: Phaser.Scene, state: AnimPreviewState, cb: SourcePanelCallbacks) {
    this.scene = scene;
    this.state = state;
    this.cb = cb;
  }

  /** Build the source panel container (call once during create) */
  buildPanel(): void {
    const scene = this.scene;
    this.sheetPanelContainer = scene.add.container(SHEET_X, SHEET_Y);
    const sheetBg = scene.add.graphics();
    const sheetHalf = SHEET_SIZE / 2;
    sheetBg.fillStyle(0x181828, 0.6);
    sheetBg.fillRect(-sheetHalf, -sheetHalf, SHEET_SIZE, SHEET_SIZE);
    drawTechRect(sheetBg, -sheetHalf, -sheetHalf, SHEET_SIZE, SHEET_SIZE, 0x334455, 0.3);
    this.sheetPanelContainer.add(sheetBg);

    scene.add.text(SHEET_X, SHEET_Y - sheetHalf - 12, 'SOURCE', {
      fontSize: '9px', fontFamily: 'monospace', fontStyle: 'bold', color: '#666688',
    }).setOrigin(0.5);
  }

  // ═══════════════════════════════════════════════════════
  // ── Sheet view: full source image with frame grid ──
  // ═══════════════════════════════════════════════════════

  refreshSheetView(): void {
    if (this.sheetViewContainer) { this.sheetViewContainer.destroy(); this.sheetViewContainer = null; }
    const st = this.state;
    if (!st.selectedSheet || !st.animDef) return;

    const scene = this.scene;
    const tex = scene.textures.get(st.selectedSheet);
    if (!tex) return;
    const source = tex.getSourceImage();
    const imgW = source.width;
    const imgH = source.height;
    if (!imgW || !imgH) return;

    const fw = st.animDef.frameWidth;
    const fh = st.animDef.frameHeight;
    const cols = Math.floor(imgW / fw);
    const rows = Math.floor(imgH / fh);

    const scale = Math.min(SHEET_SIZE / imgW, SHEET_SIZE / imgH);

    this.sheetViewContainer = scene.add.container(0, 0);
    this.sheetPanelContainer.add(this.sheetViewContainer);

    const fullImg = scene.add.image(0, 0, st.selectedSheet, '__BASE');
    fullImg.setScale(scale);
    fullImg.setAlpha(0.9);
    this.sheetViewContainer.add(fullImg);

    const grid = scene.add.graphics();
    const startX = -imgW * scale / 2;
    const startY = -imgH * scale / 2;

    grid.lineStyle(1, 0xff8844, 0.4);
    for (let c = 0; c <= cols; c++) {
      const x = startX + c * fw * scale;
      grid.lineBetween(x, startY, x, startY + rows * fh * scale);
    }
    for (let r = 0; r <= rows; r++) {
      const y = startY + r * fh * scale;
      grid.lineBetween(startX, y, startX + cols * fw * scale, y);
    }

    if (st.selectedFrame >= 0 && st.selectedFrame < st.frameCount) {
      const sheetIdx = st.animDef.frameOrder[st.selectedFrame] ?? st.selectedFrame;
      const col = sheetIdx % cols;
      const row = Math.floor(sheetIdx / cols);
      grid.lineStyle(2, 0xffcc00, 1);
      grid.strokeRect(startX + col * fw * scale, startY + row * fh * scale, fw * scale, fh * scale);

      const def = st.animDef.frames[st.selectedFrame];
      if (def?.bboxW != null && def.bboxW > 0) {
        grid.lineStyle(2, 0x00ffaa, 0.9);
        grid.strokeRect(
          startX + col * fw * scale + (def.bboxX ?? 0) * scale,
          startY + row * fh * scale + (def.bboxY ?? 0) * scale,
          def.bboxW * scale,
          (def.bboxH ?? 0) * scale,
        );
      }
    }

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const idx = r * cols + c;
        if (idx >= cols * rows) break;
        this.sheetViewContainer.add(
          scene.add.text(startX + c * fw * scale + 2, startY + r * fh * scale + 1, `${idx}`, {
            fontSize: '7px', fontFamily: 'monospace', color: '#ffcc00',
            backgroundColor: '#00000088', padding: { x: 1, y: 0 },
          })
        );
      }
    }

    this.sheetViewContainer.add(grid);
  }

  // ═══════════════════════════════════════════════════════
  // ── Bounding box overlay with drag handles ──
  // ═══════════════════════════════════════════════════════

  /** Get the sheet view scale factor (full image -> SOURCE panel) */
  getSheetScale(): number {
    const tex = this.scene.textures.get(this.state.selectedSheet);
    const source = tex?.getSourceImage();
    const imgW = source?.width ?? 1;
    const imgH = source?.height ?? 1;
    return Math.min(SHEET_SIZE / imgW, SHEET_SIZE / imgH);
  }

  /** Get the top-left corner of a frame cell in the sheet view (relative to sheet panel center) */
  getFrameCellOrigin(sheetIdx: number): { x: number; y: number } {
    const st = this.state;
    const tex = this.scene.textures.get(st.selectedSheet);
    const source = tex?.getSourceImage();
    const imgW = source?.width ?? 1;
    const imgH = source?.height ?? 1;
    const ss = this.getSheetScale();
    const fw = st.animDef.frameWidth;
    const fh = st.animDef.frameHeight;
    const cols = Math.floor(imgW / fw);
    const col = sheetIdx % cols;
    const row = Math.floor(sheetIdx / cols);
    return {
      x: -imgW * ss / 2 + col * fw * ss,
      y: -imgH * ss / 2 + row * fh * ss,
    };
  }

  /** Update bbox graphics + handle positions without destroying them (in sheet-space) */
  updateBboxVisuals(): void {
    const fi = this.bboxFrameIdx;
    if (fi < 0 || !this.bboxGfx) return;
    const def = this.state.animDef?.frames[fi];
    if (!def || def.bboxW == null || def.bboxH == null) return;

    const ss = this.getSheetScale();
    const fw = this.state.animDef.frameWidth;
    const fh = this.state.animDef.frameHeight;
    const sheetIdx = this.state.animDef.frameOrder[fi] ?? fi;
    const cell = this.getFrameCellOrigin(sheetIdx);

    const bx = cell.x + (def.bboxX ?? 0) * ss;
    const by = cell.y + (def.bboxY ?? 0) * ss;
    const bw = def.bboxW * ss;
    const bh = def.bboxH * ss;
    const cx = cell.x;
    const cy = cell.y;
    const cw = fw * ss;
    const ch = fh * ss;

    this.bboxGfx.clear();
    this.bboxGfx.lineStyle(2, 0x00ffaa, 0.9);
    this.bboxGfx.strokeRect(bx, by, bw, bh);
    this.bboxGfx.fillStyle(0x000000, 0.35);
    this.bboxGfx.fillRect(cx, cy, cw, (def.bboxY ?? 0) * ss);
    this.bboxGfx.fillRect(cx, by + bh, cw, ch - (def.bboxY ?? 0) * ss - bh);
    this.bboxGfx.fillRect(cx, by, (def.bboxX ?? 0) * ss, bh);
    this.bboxGfx.fillRect(bx + bw, by, cw - (def.bboxX ?? 0) * ss - bw, bh);

    const positions = [
      { x: bx, y: by },
      { x: bx + bw, y: by },
      { x: bx, y: by + bh },
      { x: bx + bw, y: by + bh },
    ];
    for (let i = 0; i < this.bboxHandles.length; i++) {
      this.bboxHandles[i].setPosition(positions[i].x, positions[i].y);
    }

    if (this.bboxMoveHit) {
      this.bboxMoveHit.setPosition(bx + bw / 2, by + bh / 2);
      this.bboxMoveHit.setSize(Math.max(4, bw - 16), Math.max(4, bh - 16));
    }

    if (this.bboxLabel) {
      this.bboxLabel.setText(`${def.bboxW}x${def.bboxH}`);
      this.bboxLabel.setPosition(bx + bw / 2, by - 6);
    }
  }

  /** Create the bbox overlay with handles on the SOURCE panel */
  drawBboxOverlay(frameOrderIdx: number): void {
    if (this.bboxContainer) { this.bboxContainer.destroy(); this.bboxContainer = null; }
    this.bboxHandles = [];
    this.bboxMoveHit = null;
    this.bboxGfx = null;
    this.bboxLabel = null;
    this.bboxFrameIdx = frameOrderIdx;
    const st = this.state;
    if (st.playing || frameOrderIdx < 0) return;

    const def = st.animDef?.frames[frameOrderIdx];
    if (!def || def.bboxW == null || def.bboxH == null || def.bboxW <= 0) return;

    const scene = this.scene;
    const ss = this.getSheetScale();

    this.bboxContainer = scene.add.container(SHEET_X, SHEET_Y).setDepth(15);

    this.bboxGfx = scene.add.graphics();
    this.bboxContainer.add(this.bboxGfx);

    this.bboxLabel = scene.add.text(0, 0, '', {
      fontSize: '8px', fontFamily: 'monospace', color: '#00ffaa',
    }).setOrigin(0.5, 1);
    this.bboxContainer.add(this.bboxLabel);

    this.updateBboxVisuals();

    const onDragUpdate = () => {
      this.cb.autoSave();
      this.cb.refreshPreviewSprite(frameOrderIdx);
      this.updateBboxVisuals();
    };

    let ptrStartX = 0;
    let ptrStartY = 0;

    // Corner handles
    const handleSize = 8;
    const cornerIds: Array<'tl' | 'tr' | 'bl' | 'br'> = ['tl', 'tr', 'bl', 'br'];
    for (let i = 0; i < 4; i++) {
      const cid = cornerIds[i];
      const handle = scene.add.rectangle(0, 0, handleSize, handleSize, 0x00ffaa, 0.9)
        .setInteractive({ useHandCursor: true, draggable: true });
      handle.on('dragstart', (ptr: Phaser.Input.Pointer) => {
        this.bboxDragging = cid;
        ptrStartX = ptr.x;
        ptrStartY = ptr.y;
        this.bboxDragStart = {
          x: 0, y: 0,
          bx: def.bboxX ?? 0, by: def.bboxY ?? 0,
          bw: def.bboxW!, bh: def.bboxH!,
        };
      });
      handle.on('drag', (ptr: Phaser.Input.Pointer) => {
        const ddx = (ptr.x - ptrStartX) / ss;
        const ddy = (ptr.y - ptrStartY) / ss;
        const s = this.bboxDragStart;
        const minSize = 8;
        if (this.bboxDragging === 'tl') {
          def.bboxX = Math.round(s.bx + ddx);
          def.bboxY = Math.round(s.by + ddy);
          def.bboxW = Math.max(minSize, Math.round(s.bw - ddx));
          def.bboxH = Math.max(minSize, Math.round(s.bh - ddy));
        } else if (this.bboxDragging === 'tr') {
          def.bboxY = Math.round(s.by + ddy);
          def.bboxW = Math.max(minSize, Math.round(s.bw + ddx));
          def.bboxH = Math.max(minSize, Math.round(s.bh - ddy));
        } else if (this.bboxDragging === 'bl') {
          def.bboxX = Math.round(s.bx + ddx);
          def.bboxW = Math.max(minSize, Math.round(s.bw - ddx));
          def.bboxH = Math.max(minSize, Math.round(s.bh + ddy));
        } else if (this.bboxDragging === 'br') {
          def.bboxW = Math.max(minSize, Math.round(s.bw + ddx));
          def.bboxH = Math.max(minSize, Math.round(s.bh + ddy));
        }
        onDragUpdate();
      });
      handle.on('dragend', () => {
        this.bboxDragging = null;
        this.cb.refreshFrameProps();
        this.cb.refreshSheetView();
      });
      this.bboxHandles.push(handle);
      this.bboxContainer.add(handle);
    }

    // Center move handle
    this.bboxMoveHit = scene.add.rectangle(0, 0, 10, 10, 0x000000, 0)
      .setInteractive({ useHandCursor: true, draggable: true });
    this.bboxMoveHit.on('dragstart', (ptr: Phaser.Input.Pointer) => {
      this.bboxDragging = 'move';
      ptrStartX = ptr.x;
      ptrStartY = ptr.y;
      this.bboxDragStart = {
        x: 0, y: 0,
        bx: def.bboxX ?? 0, by: def.bboxY ?? 0,
        bw: def.bboxW!, bh: def.bboxH!,
      };
    });
    this.bboxMoveHit.on('drag', (ptr: Phaser.Input.Pointer) => {
      const s = this.bboxDragStart;
      def.bboxX = Math.round(s.bx + (ptr.x - ptrStartX) / ss);
      def.bboxY = Math.round(s.by + (ptr.y - ptrStartY) / ss);
      onDragUpdate();
    });
    this.bboxMoveHit.on('dragend', () => {
      this.bboxDragging = null;
      this.cb.refreshFrameProps();
      this.cb.refreshSheetView();
    });
    this.bboxContainer.add(this.bboxMoveHit);
    this.updateBboxVisuals();
  }

  /** Whether a bbox drag is currently in progress */
  get isDragging(): boolean {
    return this.bboxDragging !== null;
  }

  // ═══════════════════════════════════════════════════════
  // ── Pixel analysis utilities ──
  // ═══════════════════════════════════════════════════════

  /** Compute the non-transparent pixel bounding box for a spritesheet frame */
  computeFrameBounds(sheetKey: string, frameIdx: number):
    { minX: number; minY: number; maxX: number; maxY: number; w: number; h: number } | null {
    const tex = this.scene.textures.get(sheetKey);
    const frame = tex?.get(frameIdx);
    if (!frame) return null;
    const source = frame.source.image as HTMLImageElement;
    const cd = frame.canvasData as { x: number; y: number; width: number; height: number };

    const canvas = document.createElement('canvas');
    canvas.width = cd.width;
    canvas.height = cd.height;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(source, cd.x, cd.y, cd.width, cd.height, 0, 0, cd.width, cd.height);
    const imgData = ctx.getImageData(0, 0, cd.width, cd.height);
    const data = imgData.data;

    let minX = cd.width, minY = cd.height, maxX = 0, maxY = 0;
    for (let py = 0; py < cd.height; py++) {
      for (let px = 0; px < cd.width; px++) {
        if (data[(py * cd.width + px) * 4 + 3] > 10) {
          if (px < minX) minX = px;
          if (px > maxX) maxX = px;
          if (py < minY) minY = py;
          if (py > maxY) maxY = py;
        }
      }
    }
    if (maxX < minX) return null;
    return { minX, minY, maxX, maxY, w: maxX - minX + 1, h: maxY - minY + 1 };
  }

  /**
   * Compute bounding box of non-transparent pixels in an extended region around a frame.
   * Reads a wider area from the source image (2x frame size, clamped to image bounds).
   * Returns bounds relative to the frame's top-left (can be negative or > fw/fh).
   */
  computeExtendedBounds(sheetKey: string, frameIdx: number):
    { minX: number; minY: number; maxX: number; maxY: number; w: number; h: number } | null {
    const tex = this.scene.textures.get(sheetKey);
    const frame = tex?.get(frameIdx);
    if (!frame) return null;
    const source = frame.source.image as HTMLImageElement;
    const cd = frame.canvasData as { x: number; y: number; width: number; height: number };
    const imgW = source.width;
    const imgH = source.height;

    const margin = Math.max(cd.width, cd.height);
    const readX = Math.max(0, cd.x - margin);
    const readY = Math.max(0, cd.y - margin);
    const readR = Math.min(imgW, cd.x + cd.width + margin);
    const readB = Math.min(imgH, cd.y + cd.height + margin);
    const readW = readR - readX;
    const readH = readB - readY;

    const canvas = document.createElement('canvas');
    canvas.width = readW;
    canvas.height = readH;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(source, readX, readY, readW, readH, 0, 0, readW, readH);
    const imgData = ctx.getImageData(0, 0, readW, readH);
    const data = imgData.data;

    let minPx = readW, minPy = readH, maxPx = 0, maxPy = 0;
    for (let py = 0; py < readH; py++) {
      for (let px = 0; px < readW; px++) {
        if (data[(py * readW + px) * 4 + 3] > 10) {
          if (px < minPx) minPx = px;
          if (px > maxPx) maxPx = px;
          if (py < minPy) minPy = py;
          if (py > maxPy) maxPy = py;
        }
      }
    }
    if (maxPx < minPx) return null;

    const frameOffX = cd.x - readX;
    const frameOffY = cd.y - readY;
    return {
      minX: minPx - frameOffX,
      minY: minPy - frameOffY,
      maxX: maxPx - frameOffX,
      maxY: maxPy - frameOffY,
      w: maxPx - minPx + 1,
      h: maxPy - minPy + 1,
    };
  }

  /**
   * Flood-fill based sprite segmentation for a single frame.
   * 1. Compute center of mass of opaque pixels
   * 2. Find the seed pixel closest to COM
   * 3. BFS flood-fill from seed, visiting 4-connected opaque neighbors
   *    (uses 1px dilated alpha mask to bridge anti-aliased gaps)
   * 4. Return bounds of the connected component
   */
  floodFillBounds(sheetKey: string, frameIdx: number):
    { cx: number; cy: number; minX: number; minY: number; maxX: number; maxY: number; w: number; h: number } | null {
    const tex = this.scene.textures.get(sheetKey);
    const frame = tex?.get(frameIdx);
    if (!frame) return null;
    const source = frame.source.image as HTMLImageElement;
    const cd = frame.canvasData as { x: number; y: number; width: number; height: number };
    const W = cd.width;
    const H = cd.height;

    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(source, cd.x, cd.y, W, H, 0, 0, W, H);
    const data = ctx.getImageData(0, 0, W, H).data;

    const opaque = new Uint8Array(W * H);
    let totalAlpha = 0, sumX = 0, sumY = 0;
    for (let py = 0; py < H; py++) {
      for (let px = 0; px < W; px++) {
        const a = data[(py * W + px) * 4 + 3];
        if (a > 10) {
          opaque[py * W + px] = 1;
          totalAlpha += a;
          sumX += px * a;
          sumY += py * a;
        }
      }
    }
    if (totalAlpha === 0) return null;
    const comX = sumX / totalAlpha;
    const comY = sumY / totalAlpha;

    const dilated = new Uint8Array(W * H);
    for (let py = 0; py < H; py++) {
      for (let px = 0; px < W; px++) {
        if (opaque[py * W + px]) { dilated[py * W + px] = 1; continue; }
        if ((px > 0 && opaque[py * W + px - 1]) ||
            (px < W - 1 && opaque[py * W + px + 1]) ||
            (py > 0 && opaque[(py - 1) * W + px]) ||
            (py < H - 1 && opaque[(py + 1) * W + px])) {
          dilated[py * W + px] = 1;
        }
      }
    }

    let seedX = Math.round(comX), seedY = Math.round(comY);
    if (!opaque[seedY * W + seedX]) {
      let bestDist = Infinity;
      for (let py = 0; py < H; py++) {
        for (let px = 0; px < W; px++) {
          if (opaque[py * W + px]) {
            const d = (px - comX) ** 2 + (py - comY) ** 2;
            if (d < bestDist) { bestDist = d; seedX = px; seedY = py; }
          }
        }
      }
    }

    const visited = new Uint8Array(W * H);
    const queue: number[] = [seedY * W + seedX];
    visited[seedY * W + seedX] = 1;
    let minX = W, minY = H, maxX = 0, maxY = 0;

    while (queue.length > 0) {
      const idx = queue.shift()!;
      const px = idx % W;
      const py = (idx - px) / W;

      if (opaque[idx]) {
        if (px < minX) minX = px;
        if (px > maxX) maxX = px;
        if (py < minY) minY = py;
        if (py > maxY) maxY = py;
      }

      const neighbors = [
        py > 0 ? idx - W : -1,
        py < H - 1 ? idx + W : -1,
        px > 0 ? idx - 1 : -1,
        px < W - 1 ? idx + 1 : -1,
      ];
      for (const ni of neighbors) {
        if (ni >= 0 && !visited[ni] && dilated[ni]) {
          visited[ni] = 1;
          queue.push(ni);
        }
      }
    }

    if (maxX < minX) return null;

    let ccSumX = 0, ccSumY = 0, ccCount = 0;
    for (let py = minY; py <= maxY; py++) {
      for (let px = minX; px <= maxX; px++) {
        if (visited[py * W + px] && opaque[py * W + px]) {
          ccSumX += px; ccSumY += py; ccCount++;
        }
      }
    }
    const cx = ccCount > 0 ? ccSumX / ccCount : comX;
    const cy = ccCount > 0 ? ccSumY / ccCount : comY;

    return { cx, cy, minX, minY, maxX, maxY, w: maxX - minX + 1, h: maxY - minY + 1 };
  }

  /**
   * Auto-fit: flood-fill based segmentation.
   * 1. Flood-fill from COM to find connected sprite bounds per frame
   * 2. Use median bounds size as unified bbox (consistent across frames)
   * 3. Center bbox on each frame's connected component centroid
   */
  autoFitFrames(): void {
    const st = this.state;
    if (!st.animDef || st.frameCount === 0) return;

    const order = st.animDef.frameOrder;

    const fills: (ReturnType<SourcePanel['floodFillBounds']>)[] = [];
    const widths: number[] = [];
    const heights: number[] = [];
    for (let i = 0; i < st.frameCount; i++) {
      const sheetIdx = order[i] ?? i;
      const fb = this.floodFillBounds(st.selectedSheet, sheetIdx);
      fills.push(fb);
      if (fb) {
        widths.push(fb.w + 4);
        heights.push(fb.h + 4);
      }
    }

    if (widths.length === 0) return;

    widths.sort((a, b) => a - b);
    heights.sort((a, b) => a - b);
    const medianW = widths[Math.floor(widths.length / 2)];
    const medianH = heights[Math.floor(heights.length / 2)];

    for (let i = 0; i < st.frameCount; i++) {
      const fb = fills[i];
      const frameDef = st.animDef.frames[i];
      if (!fb || !frameDef) continue;

      frameDef.bboxX = Math.round(fb.cx - medianW / 2);
      frameDef.bboxY = Math.round(fb.cy - medianH / 2);
      frameDef.bboxW = medianW;
      frameDef.bboxH = medianH;
      frameDef.offsetX = 0;
      frameDef.offsetY = 0;
      frameDef.scale = 1;
    }

    this.cb.autoSave();
  }
}
